/**
 * Centralized logging utility for the frontend application.
 * Provides structured logging with correlation ID support and environment-aware behavior.
 */

/**
 * Log levels
 */
const LogLevel = {
  ERROR: 'ERROR',
  WARN: 'WARN',
  INFO: 'INFO',
  DEBUG: 'DEBUG'
};

/**
 * Check if we're in development mode
 */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Format a log message with timestamp and context
 */
function formatLogMessage(level, message, context = {}) {
  const timestamp = new Date().toISOString();
  const correlationId = context.correlationId || 'N/A';

  let formatted = `[${level}] ${timestamp} - ${message}`;

  if (correlationId !== 'N/A') {
    formatted += `\n  CorrelationId: ${correlationId}`;
  }

  // Add additional context fields
  const additionalContext = Object.entries(context)
    .filter(([key]) => key !== 'correlationId')
    .map(([key, value]) => `  ${key}: ${JSON.stringify(value)}`)
    .join('\n');

  if (additionalContext) {
    formatted += `\n${additionalContext}`;
  }

  return formatted;
}

/**
 * Extract correlation ID from error response
 */
function extractCorrelationId(error) {
  // Try to get from response headers
  if (error?.response?.headers?.['x-correlation-id']) {
    return error.response.headers['x-correlation-id'];
  }

  // Try to get from response body
  if (error?.response?.data?.correlationId) {
    return error.response.data.correlationId;
  }

  return null;
}

/**
 * Logger object with methods for different log levels
 */
export const logger = {
  /**
   * Log error messages
   * Always logs, even in production
   */
  error: (message, error = null, context = {}) => {
    const correlationId = error ? extractCorrelationId(error) : context.correlationId;
    const enrichedContext = {
      ...context,
      correlationId: correlationId || context.correlationId
    };

    // Add error details if available
    if (error) {
      enrichedContext.status = error?.response?.status;
      enrichedContext.statusText = error?.response?.statusText;
      enrichedContext.message = error?.message;
      enrichedContext.endpoint = error?.config?.url;
      enrichedContext.method = error?.config?.method;
    }

    console.error(formatLogMessage(LogLevel.ERROR, message, enrichedContext));

    // Log full error object in development
    if (isDevelopment && error) {
      console.error('Error details:', error);
    }
  },

  /**
   * Log warning messages
   * Always logs, even in production
   */
  warn: (message, context = {}) => {
    console.warn(formatLogMessage(LogLevel.WARN, message, context));
  },

  /**
   * Log informational messages
   * Only logs in development
   */
  info: (message, context = {}) => {
    if (isDevelopment) {
      console.info(formatLogMessage(LogLevel.INFO, message, context));
    }
  },

  /**
   * Log debug messages
   * Only logs in development
   */
  debug: (message, context = {}) => {
    if (isDevelopment) {
      console.log(formatLogMessage(LogLevel.DEBUG, message, context));
    }
  }
};

export default logger;
